import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { QrCode } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/tracking";
import TurnstileWidget, { turnstileConfigured } from "@/components/TurnstileWidget";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      if (turnstileConfigured() && !turnstileToken) {
        toast.error("Please complete the security check");
        return;
      }
      const result = await signup(email, password, turnstileToken, companyWebsite);
      trackEvent("signup_success");
      if (
        window.localStorage.getItem("smartqr_measurement_consent") === "granted" &&
        typeof window.gtag === "function"
      ) {
        window.gtag("event", "conversion", {
          send_to: "AW-18380453147/fFhaCOyQ9t4cEJvqvbxE",
        });
      }
      // Redirect to check-email page instead of directly to onboarding
      navigate("/check-email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in">
        <Card className="border-border">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="flex items-center justify-center gap-2 mb-6" data-testid="signup-logo-link">
              <QrCode className="text-primary" size={28} />
              <span className="font-heading text-xl font-bold">Smart-QR</span>
            </Link>
            <CardTitle className="font-heading text-2xl">Create your account</CardTitle>
            <CardDescription>Get started with Smart-QR in seconds</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="signup-email-input"
                />
              </div>
              <div className="absolute -left-[10000px]" aria-hidden="true">
                <Label htmlFor="company-website">Company website</Label>
                <Input
                  id="company-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                />
              </div>
              <TurnstileWidget onToken={setTurnstileToken} />
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="signup-password-input"
                />
              </div>
              <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="signup-submit-btn">
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="signup-login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
